/**
 * CopyButton — copy a value, confirm it happened, and say so out loud.
 *
 * The icon swaps to a check for ~1.4s. The control's box never changes size
 * because both glyphs are the same optical weight in the same slot, and the
 * result is spoken through the app's single polite live region.
 *
 * That region is deliberately *not* a child of this component. One instance per
 * row meant a virtualised grid mounted and unmounted 32 empty `aria-live` nodes
 * on every scroll frame; `lib/announce.ts` owns one node for the whole app.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { announce } from '@/lib/announce';
import { cn } from '@/lib/cn';
import { Button } from './Button';
import type { ButtonVariant, ControlSize } from './Button';
import { IconButton } from './IconButton';

const FEEDBACK_MS = 1400;

type CopyState = 'idle' | 'copied' | 'error';

async function writeToClipboard(value: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    /* fall through to the legacy path */
  }

  try {
    const scratch = document.createElement('textarea');
    scratch.value = value;
    scratch.setAttribute('readonly', '');
    scratch.style.position = 'fixed';
    scratch.style.opacity = '0';
    document.body.appendChild(scratch);
    scratch.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(scratch);
    return ok;
  } catch {
    return false;
  }
}

export interface CopyButtonProps {
  value: string;
  /** Accessible name and tooltip — say *what* is copied ("Copy email"). */
  label?: string;
  copiedLabel?: string;
  size?: ControlSize;
  variant?: ButtonVariant;
  /** Render a full Button with visible text instead of an icon-only control. */
  withLabel?: boolean;
  className?: string;
  onCopied?: (value: string) => void;
  /**
   * `-1` takes the control out of the page's tab ring — what a per-row control
   * inside a `role="grid"` needs, where the grid is one tab stop and its cell
   * controls are reached with → and activated with Enter.
   */
  tabIndex?: number;
}

export function CopyButton({
  value,
  label = 'Copy',
  copiedLabel = 'Copied',
  size = 'sm',
  variant = 'ghost',
  withLabel = false,
  className,
  onCopied,
  tabIndex,
}: CopyButtonProps) {
  const [state, setState] = useState<CopyState>('idle');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(
    () => () => {
      if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
    },
    [],
  );

  const copy = useCallback(async () => {
    const ok = await writeToClipboard(value);
    setState(ok ? 'copied' : 'error');
    if (ok) onCopied?.(value);
    // "Copy Adaeze Sankara's email" → "Copied Adaeze Sankara's email": the label
    // already names the thing, so the past tense of it is the announcement.
    announce(
      ok
        ? /^copy\b/i.test(label)
          ? label.replace(/^copy\b/i, copiedLabel)
          : `${copiedLabel}: ${label}`
        : 'Copy failed',
    );
    if (timeoutRef.current !== null) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setState('idle'), FEEDBACK_MS);
  }, [copiedLabel, label, onCopied, value]);

  const copied = state === 'copied';
  const currentLabel = copied ? copiedLabel : state === 'error' ? 'Copy failed' : label;
  const icon = copied ? (
    <Check aria-hidden="true" className="text-success" strokeWidth={2.75} />
  ) : (
    <Copy aria-hidden="true" />
  );

  return withLabel ? (
    <Button
      variant={variant}
      size={size}
      iconLeft={icon}
      onClick={() => void copy()}
      tabIndex={tabIndex}
      className={className}
    >
      {currentLabel}
    </Button>
  ) : (
    <IconButton
      label={currentLabel}
      variant={variant}
      size={size}
      onClick={() => void copy()}
      tabIndex={tabIndex}
      className={cn(state === 'error' && 'text-danger', className)}
    >
      {icon}
    </IconButton>
  );
}
